import { PrintRow } from '~Components/PrinterManager';

/**
 * Layout engine for prints on Adyen terminals (S1F2 and similar handhelds).
 *
 * The built-in printer is a 58mm thermal head with 384 dots across. Adyen's text print
 * format gives us no control over fonts or alignment, so instead we rasterise every print
 * ourselves and send it as an image. This module is the pure, canvas-free half of that:
 * it decides, for every row, how many character columns the row occupies, how big each
 * character cell is in dots, and how the text is padded so the columns line up exactly.
 *
 * The rasteriser (render.ts) then draws each character in its own cell, so column
 * alignment is guaranteed by geometry rather than by whatever font the device happens to
 * resolve `monospace` to.
 */

/** Dots across the paper. Adyen documents 384px as the max image width for 58mm paper. */
export const ADYEN_PAPER_WIDTH_DOTS = 384;

/**
 * Width passed to @baemingo/receipt when generating for an Adyen terminal.
 * The receipt library pads "normal" lines to `width` characters and "condensed" lines
 * to `width * 2`, so 16 gives us 16 large columns or 32 standard columns per line.
 */
export const ADYEN_RECEIPT_WIDTH = 16;

/** Standard columns: 32 chars of 12x24 dots, the classic 58mm receipt font. */
export const STANDARD_COLUMNS = 32;

/** Large columns: 16 chars of 24x48 dots, used for headings and the order number. */
export const LARGE_COLUMNS = 16;

/**
 * Column counts the receipt library can produce for width 16
 * (double width / normal / condensed / condensed on a wider template).
 * A row whose length matches one of these is treated as already padded.
 */
const KNOWN_COLUMN_COUNTS = [8, 16, 32, 64];

/**
 * Receipt callers encode "double height" as fontSize 26 and normal as 13, other prints
 * use free font sizes. Anything at or above this is rendered as a large row.
 */
const LARGE_FONT_SIZE_THRESHOLD = 20;

/** Height of a blank spacer row, regardless of its nominal cell size. */
const SPACER_HEIGHT_DOTS = 24;

/**
 * Max height of one image sent to the terminal. Long receipts are split into several
 * sequential print requests; each is well within Adyen's payload limits.
 */
export const MAX_SEGMENT_HEIGHT_DOTS = 800;

export interface LayoutLine {
  /** Text padded (or wrapped) to exactly `columns` characters. */
  text: string;
  /** Number of character cells on this line. */
  columns: number;
  /** Width of one character cell in dots. */
  cellWidth: number;
  /** Height of the line in dots. */
  cellHeight: number;
  bold: boolean;
}

export type PrintSource = string | PrintRow[] | undefined;

/** Turns a plain-string print (z-reports, test prints) into left-aligned rows. */
const rowsFromString = (text: string): PrintRow[] =>
  text.split('\n').map((line) => ({ content: line.replace(/\r$/, ''), alignment: 'LEFT' }));

const isLargeRow = (row: PrintRow) =>
  Boolean(row.doubleWidth) ||
  Boolean(row.doubleHeigth) ||
  (row.fontSize !== undefined && row.fontSize >= LARGE_FONT_SIZE_THRESHOLD);

/** Pads `text` to `columns` chars according to alignment. Never truncates. */
export const padToColumns = (text: string, columns: number, alignment?: PrintRow['alignment']) => {
  const missing = columns - text.length;
  if (missing <= 0) {
    return text;
  }

  if (alignment === 'RIGHT') {
    return ' '.repeat(missing) + text;
  }

  if (alignment === 'CENTER') {
    const left = Math.floor(missing / 2);
    return ' '.repeat(left) + text + ' '.repeat(missing - left);
  }

  return text + ' '.repeat(missing);
};

/**
 * Wraps text into chunks of at most `columns` chars, breaking on spaces where possible.
 * Used for rows that were not pre-formatted by the receipt library (bongs, reports).
 */
export const wrapToColumns = (text: string, columns: number): string[] => {
  if (text.length <= columns) {
    return [text];
  }

  const lines: string[] = [];
  let remaining = text;

  while (remaining.length > columns) {
    const window = remaining.slice(0, columns + 1);
    const breakAt = window.lastIndexOf(' ');
    const cut = breakAt > 0 ? breakAt : columns;

    lines.push(remaining.slice(0, cut).trimEnd());
    remaining = remaining.slice(cut).trimStart();
  }

  if (remaining.length) {
    lines.push(remaining);
  }

  return lines;
};

/**
 * Decides the column count for a row.
 * Pre-padded receipt rows keep their exact length so the library's alignment survives;
 * everything else gets the standard or large grid.
 */
const columnsForRow = (row: PrintRow) => {
  const trimmedLength = row.content.trimEnd().length;

  if (trimmedLength > 0 && KNOWN_COLUMN_COUNTS.includes(row.content.length)) {
    return row.content.length;
  }

  return isLargeRow(row) ? LARGE_COLUMNS : STANDARD_COLUMNS;
};

/** Converts a print payload into positioned lines ready for rasterising. QR rows are skipped. */
export const layoutRows = (source: PrintSource): LayoutLine[] => {
  if (!source) {
    return [];
  }

  const rows = typeof source === 'string' ? rowsFromString(source) : source;

  return rows.reduce<LayoutLine[]>((lines, row) => {
    if (row.qr) {
      return lines;
    }

    const columns = columnsForRow(row);
    const cellWidth = ADYEN_PAPER_WIDTH_DOTS / columns;
    const large = isLargeRow(row);

    // A cell is twice as tall as it is wide (12x24, 24x48). A large row on the standard grid
    // (e.g. a condensed double-height title) doubles the height instead of the width.
    const baseHeight = cellWidth * 2;
    const cellHeight = large && columns >= STANDARD_COLUMNS ? baseHeight * 2 : baseHeight;

    const isPrePadded = row.content.length === columns;
    const chunks = isPrePadded ? [row.content] : wrapToColumns(row.content, columns);

    chunks.forEach((chunk) => {
      const text = padToColumns(chunk, columns, row.alignment);
      const isSpacer = text.trim().length === 0;

      lines.push({
        text,
        columns,
        cellWidth,
        cellHeight: isSpacer ? Math.min(cellHeight, SPACER_HEIGHT_DOTS) : cellHeight,
        bold: Boolean(row.bold),
      });
    });

    return lines;
  }, []);
};

/** Total height in dots of a set of lines. */
export const linesHeight = (lines: LayoutLine[]) =>
  lines.reduce((sum, line) => sum + line.cellHeight, 0);

/**
 * Splits lines into consecutive segments no taller than `maxHeight`, never cutting a line.
 * A single line taller than `maxHeight` gets a segment of its own.
 */
export const segmentLines = (
  lines: LayoutLine[],
  maxHeight = MAX_SEGMENT_HEIGHT_DOTS,
): LayoutLine[][] => {
  const segments: LayoutLine[][] = [];
  let current: LayoutLine[] = [];
  let currentHeight = 0;

  lines.forEach((line) => {
    if (current.length && currentHeight + line.cellHeight > maxHeight) {
      segments.push(current);
      current = [];
      currentHeight = 0;
    }

    current.push(line);
    currentHeight += line.cellHeight;
  });

  if (current.length) {
    segments.push(current);
  }

  return segments;
};
